"use client";

import { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "inverse" | "danger";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-secondary-fixed text-on-secondary-fixed focus:ring-secondary/20 font-extrabold eco-glow btn-hover-primary",
  secondary: "glass-light text-primary hover:bg-white/70 focus:ring-primary/20 btn-hover-secondary",
  outline:
    "border border-surface-dim text-foreground bg-transparent hover:bg-surface-container-low focus:ring-primary/20 btn-hover-secondary",
  ghost:
    "text-primary bg-transparent hover:bg-primary/10 focus:ring-primary/20 btn-hover-secondary",
  inverse:
    "bg-white text-primary hover:bg-primary hover:text-white focus:ring-white/20 btn-hover-secondary",
  danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-600/20 btn-hover-secondary",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-sm gap-1.5",
  md: "px-4 py-2 text-base gap-2",
  lg: "px-6 py-3 text-lg gap-2",
};

/**
 * Button - Pill-shaped button with Verdant Authority styling
 *
 * Variants:
 * - primary: High-impact CTA (neon #6dfe9c bg, dark text, extrabold, eco-glow)
 * - secondary: Glass container with primary text (glassmorphism)
 * - outline: Subtle border, tonal hover (surface hierarchy)
 * - ghost: Tertiary actions (no background)
 * - inverse: CTAs on dark backgrounds (white bg, dark green hover)
 * - danger: Destructive actions (red)
 *
 * Sizes:
 * - sm: Compact buttons
 * - md: Default size
 * - lg: Prominent CTAs
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      fullWidth = false,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={cn(
          // Base styles
          "inline-flex items-center justify-center font-semibold rounded-full focus:outline-none focus:ring-2",
          // Variant styles
          variantStyles[variant],
          // Size styles
          sizeStyles[size],
          // Full width
          fullWidth && "w-full",
          // Disabled state
          isDisabled && "opacity-50 cursor-not-allowed",
          className
        )}
        {...props}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </button>
    );
  }
);

Button.displayName = "Button";

// ButtonLink variant for anchor-style buttons
interface ButtonLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    {
      variant = "primary",
      size = "md",
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <a
        ref={ref}
        className={cn(
          // Base styles
          "inline-flex items-center justify-center font-semibold rounded-full focus:outline-none focus:ring-2",
          // Variant styles
          variantStyles[variant],
          // Size styles
          sizeStyles[size],
          // Full width
          fullWidth && "w-full",
          className
        )}
        {...props}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </a>
    );
  }
);

ButtonLink.displayName = "ButtonLink";
